import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FALLBACK_GOAL_COLOR, formatAmount, hexToTint } from '../../utils/goals';
import { GOAL_ICONS } from './GoalIcon';
import { LuTarget } from 'react-icons/lu';

/**
 * A movement's goal, on the movement row: a 16px tinted disc with the goal's
 * icon, never a coloured dot — the tag pill beside it is already a row of dots,
 * and a goal dot would read as one more tag.
 *
 * A movement the attach rules no longer count keeps its mark with a warning
 * ring, because that is the one thing a goal group header cannot say.
 */
export default function GoalMark({ movement, size = 16 }) {
  const { t } = useTranslation('goals');
  const goal = movement?.goal;
  if (!goal) return null;

  const color = goal.color || FALLBACK_GOAL_COLOR;
  const Icon = GOAL_ICONS[goal.icon] || LuTarget;
  const isInvalid = goal.effect === 'invalid';
  const sign = goal.effect === 'add' ? '+' : goal.effect === 'subtract' ? '−' : '';

  const reason = t(`reasons.${goal.reason}`, { defaultValue: goal.reason });
  const label = isInvalid
    ? t('list.invalidTooltip', {
        name: goal.name,
        // The reason continues the sentence after the colon.
        reason: reason.charAt(0).toLowerCase() + reason.slice(1),
      })
    : t('list.markTooltip', {
        name: goal.name,
        effect: sign
          ? `${sign}${formatAmount(movement.amount)}`
          : t('list.noEffect'),
      });

  return (
    <Tooltip label={label} openDelay={300} hasArrow placement="top">
      <Flex
        w={`${size}px`}
        h={`${size}px`}
        borderRadius="5px"
        bg={hexToTint(color)}
        color={isInvalid ? 'redesign.accent.amber' : color}
        align="center"
        justify="center"
        flexShrink={0}
        border={isInvalid ? '1.5px solid' : undefined}
        borderColor={isInvalid ? 'redesign.accent.amber' : undefined}
      >
        <Box as={Icon} boxSize={`${Math.round(size * 0.62)}px`} />
      </Flex>
    </Tooltip>
  );
}
